import axios from 'axios';

// Get API base URL based on environment
const getApiBaseUrl = () => {
  // In development, use Vite proxy (relative URL)
  if (import.meta.env.DEV) {
    return '/api/v1';
  }

  // In production, use the deployed backend URL from environment
  const apiUrl = import.meta.env.VITE_API_URL;
  if (apiUrl) {
    // Ensure the URL ends with /api/v1 if it's not already included
    if (apiUrl.endsWith('/api/v1')) {
      return apiUrl;
    } if (apiUrl.endsWith('/')) {
      return `${apiUrl}api/v1`;
    }
    return `${apiUrl}/api/v1`;
  }

  // Fallback to localhost for development or if no env var is set
  return 'http://localhost:5001/api/v1';
};

export const API_BASE = getApiBaseUrl();

let onSessionExpired = null;
export function setSessionExpiredHandler(handler) {
  onSessionExpired = handler;
}

// Create axios instance with credentials enabled
const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  responseType: 'json',
  withCredentials: true, // Ensure cookies are sent for CORS
});

// Allow registering an external async token getter (e.g., Clerk)
let externalTokenGetter = null;
export function setExternalTokenGetter(getterFn) {
  externalTokenGetter = getterFn;
}

// Request Interceptor: Attach token (supports async external getter)
api.interceptors.request.use(
  async (config) => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (token) {
      // eslint-disable-next-line no-param-reassign
      config.headers.Authorization = `Bearer ${token}`;
      return config;
    }
    if (externalTokenGetter) {
      try {
        const extToken = await externalTokenGetter();
        if (extToken) {
          // eslint-disable-next-line no-param-reassign
          config.headers.Authorization = `Bearer ${extToken}`;
        }
      } catch (e) {
        // ignore, proceed without header
      }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Helper: Refresh token
async function refreshAccessToken() {
  const refreshToken = typeof window !== 'undefined' ? localStorage.getItem('refreshToken') : null;
  if (!refreshToken) throw new Error('No refresh token available');
  try {
    const { data } = await api.post('/auth/refresh', { refreshToken });
    const { accessToken, refreshToken: newRefreshToken } = data;
    localStorage.setItem('token', accessToken);
    localStorage.setItem('refreshToken', newRefreshToken);
    return accessToken;
  } catch (error) {
    localStorage.clear();
    if (onSessionExpired) onSessionExpired();
    window.location.href = '/login';
    throw error;
  }
}

// Response Interceptor: Handle 401/403 + token refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => (error ? prom.reject(error) : prom.resolve(token)));
  failedQueue = [];
};

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config;
    if (
      error.response
      && [401, 403].includes(error.response.status)
      // eslint-disable-next-line no-underscore-dangle
      && !originalRequest._retry
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }
      // eslint-disable-next-line no-underscore-dangle
      originalRequest._retry = true;
      isRefreshing = true;
      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        processQueue(null, newToken);
        return api(originalRequest);
      } catch (err) {
        processQueue(err, null);
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  },
);

// Auth helpers
export const isAuthenticated = () => !!(localStorage.getItem('token') || localStorage.getItem('refreshToken'));

export const logout = async () => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (refreshToken) {
    try {
      await api.post('/auth/logout', { refreshToken });
    } catch (err) {
      // ignore
    }
  }
  localStorage.clear();
  window.location.href = '/login';
};

export default api;
